import { createHttpClient } from '@adapters/HttpClient';
import { LocationsApiAdapter, type LocationListItem } from '@modules/Products/CreateEdit/adapters/LocationsApiAdapter';
import type { DynamicComboboxOption } from '@shared/components/ui/DynamicCombobox';
import { apiClientWH } from '@shared/services';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

const PAGE_SIZE = 20;

async function fetchAllStockLocationIds(
  adapter: LocationsApiAdapter,
  productId: string | number,
): Promise<Set<string>> {
  const ids = new Set<string>();
  let page = 1;
  let hasMore = true;

  while (hasMore) {
    const result = await adapter.getPaginated({
      product_ids: String(productId),
      page,
      per_page: 100,
      order_direction: 'desc',
    });

    result.items.forEach((location) => ids.add(location.id));
    hasMore = result.hasMore;
    page += 1;
  }

  return ids;
}

function isAvailableLocation(location: LocationListItem, productId: string | number, stockLocationIds: Set<string>) {
  const currentProductId = String(productId);

  if (stockLocationIds.has(location.id)) {
    return false;
  }

  if (location.productId != null && location.productId !== currentProductId) {
    return false;
  }

  return true;
}

export function useAvailableLocationsSearch(productId: string | number | null, enabled: boolean) {
  const httpClient = useMemo(() => createHttpClient(apiClientWH), []);
  const adapter = useMemo(() => new LocationsApiAdapter(httpClient), [httpClient]);
  const pageRef = useRef(1);
  const [hasMore, setHasMore] = useState(false);

  const { data: stockLocationIds = new Set<string>() } = useQuery({
    queryKey: ['product-stock-location-ids', productId],
    enabled: enabled && Boolean(productId),
    queryFn: () => fetchAllStockLocationIds(adapter, productId!),
    staleTime: 2 * 60 * 1000,
  });

  useEffect(() => {
    pageRef.current = 1;
    setHasMore(false);
  }, [productId, enabled]);

  const searchFn = useCallback(
    async (query: string, append?: boolean): Promise<DynamicComboboxOption[]> => {
      if (!productId) return [];

      if (!append) {
        pageRef.current = 1;
      }

      const page = pageRef.current;
      const result = await adapter.getPaginated({
        search: query.trim() || undefined,
        page,
        per_page: PAGE_SIZE,
        order_direction: 'desc',
      });

      setHasMore(result.hasMore);
      if (result.hasMore) {
        pageRef.current = page + 1;
      }

      return result.items
        .filter((location) => isAvailableLocation(location, productId, stockLocationIds))
        .map((location) => ({
          value: location.id,
          label: location.name,
        }));
    },
    [adapter, productId, stockLocationIds],
  );

  return {
    searchFn,
    hasMore,
  };
}
